import { useEffect, useState, useCallback } from 'react';
import { getReceiverClaimInfo, completeReceiverClaim } from '../utils/httpApi';
import { isApiSuccess, getFirstApiError } from '../utils/api';
import { getAccessToken } from '../utils/auth';
import './ClaimReceiver.css';

// /claim/:token landing page. Walks the user through confirming a pending
// receiver registration created by their on-device aviotrack-agent.
//
// The aviotrackfe app uses page-state navigation rather than react-router,
// so this component reads the token directly from window.location.pathname
// and uses window.location.assign for post-claim navigation. App.js renders
// us when the path starts with /claim/.
//
// Flow per backend:
//   PENDING + new user  → form: password + displayName.
//   PENDING + existing  → if logged in, one-click confirm; else password.
//   CLAIMED             → "already claimed" + button to receivers page.
//   EXPIRED / UNKNOWN   → "re-run Wi-Fi setup" terminal screen.
export default function ClaimReceiver() {
  const claimToken = extractTokenFromPath();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Receiver location. Captured here rather than in the captive portal:
  // the captive portal is HTTP (on the device's AP IP) where browsers
  // block the geolocation API; this page is HTTPS — a real secure
  // context — so navigator.geolocation works.
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoMsg, setGeoMsg] = useState('');
  // Friendly receiver name — what the user will see in My Receivers and
  // on the map. The backend generates a default (airport ICAO + email
  // local-part) if we send blank.
  const [receiverName, setReceiverName] = useState('');
  // NOTE: no password / display-name state. Aviotrack is passwordless —
  // the magic-link click IS the authentication (it proves control of the
  // email). New users are created with email_verified=true and no
  // password; existing users are trusted via the single-use token.

  useEffect(() => {
    let cancelled = false;
    if (!claimToken) {
      setError('Missing claim token');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const resp = await getReceiverClaimInfo(claimToken);
        if (cancelled) return;
        if (!isApiSuccess(resp)) {
          setError(getFirstApiError(resp) || 'Invalid claim link');
        } else {
          setInfo(resp);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Network error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [claimToken]);

  // Capture the device's location from the phone's GPS. Works here
  // because the claim page is HTTPS (secure context); the same call on
  // the HTTP captive portal is blocked by the browser.
  const captureLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoMsg('This browser has no location support — enter coordinates manually below.');
      return;
    }
    setGeoBusy(true);
    setGeoMsg('Asking your device for its location…');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude.toFixed(6));
        setLongitude(pos.coords.longitude.toFixed(6));
        setGeoBusy(false);
        setGeoMsg('Location captured.');
      },
      (err) => {
        setGeoBusy(false);
        setGeoMsg(
          'Could not get your location (' + (err.message || 'permission denied') +
          '). Enter the coordinates manually below — Google Maps shows them ' +
          'when you long-press an address.'
        );
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (Number.isNaN(lat) || lat < -90 || lat > 90 ||
        Number.isNaN(lng) || lng < -180 || lng > 180) {
      setError('Please set the receiver location — tap "Use my current location" or type the coordinates.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const token = getAccessToken();
      const resp = await completeReceiverClaim(
        {
          claimToken,
          receiverName: receiverName.trim() || undefined,
          latitude: lat,
          longitude: lng,
        },
        token
      );
      if (!isApiSuccess(resp)) {
        setError(getFirstApiError(resp) || 'Could not complete claim');
        return;
      }
      // Drop them on the receivers page; their new device should be visible.
      window.location.assign(`/?receiverUuid=${encodeURIComponent(resp.uuid)}`);
    } catch (e) {
      setError(e.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  }, [claimToken, receiverName, latitude, longitude]);

  if (loading) {
    return <div className="claim-receiver"><p className="info">Loading…</p></div>;
  }

  if (!info || info.status === 'UNKNOWN' || info.status === 'EXPIRED') {
    return (
      <div className="claim-receiver">
        <h1>Confirmation link unavailable</h1>
        <p className="info terminal">
          {info?.status === 'EXPIRED'
            ? 'This confirmation link has expired.'
            : 'This confirmation link is invalid.'}
          <br />Re-run Wi-Fi setup on your device to start over.
        </p>
      </div>
    );
  }

  if (info.status === 'CLAIMED') {
    return (
      <div className="claim-receiver">
        <h1>Already confirmed</h1>
        <p className="info terminal">This receiver has already been registered. Visit your receivers page to manage it.</p>
        <button onClick={() => window.location.assign('/')}>Go to Aviotrack</button>
      </div>
    );
  }

  // PENDING — render confirmation form.
  const isNewUser = !info.isExistingUser;

  return (
    <div className="claim-receiver">
      <h1>Confirm your new receiver</h1>
      <p className="subtitle">
        {isNewUser
          ? `Welcome! Pin the receiver's location and give it a name to finish setting it up.`
          : `Pin the receiver's location and give it a name to finish setting it up.`}
      </p>
      <dl>
        <dt>Email</dt><dd>{info.email}</dd>
        <dt>Device</dt><dd>{info.hwId}</dd>
      </dl>

      {error && <div className="error">{error}</div>}

      <form onSubmit={submit}>
        <label>
          Receiver name
          <input
            type="text"
            value={receiverName}
            onChange={(e) => setReceiverName(e.target.value)}
            placeholder="e.g. Lajes home, Garage Pi…"
            maxLength={255}
            autoComplete="off"
          />
        </label>

        <div className="location-block">
          <p className="location-label">Where is the receiver installed?</p>
          <button
            type="button"
            className="secondary"
            onClick={captureLocation}
            disabled={geoBusy}
          >
            {geoBusy ? 'Locating…' : 'Use my current location'}
          </button>
          {geoMsg && <p className="info">{geoMsg}</p>}
          <div className="coord-row">
            <label>
              Latitude
              <input
                type="number"
                step="0.000001"
                min="-90"
                max="90"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="e.g. 38.7600"
                required
              />
            </label>
            <label>
              Longitude
              <input
                type="number"
                step="0.000001"
                min="-180"
                max="180"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="e.g. -27.0900"
                required
              />
            </label>
          </div>
        </div>

        <button type="submit" disabled={submitting}>
          {submitting ? 'Confirming…' : 'Confirm this is mine'}
        </button>
      </form>
    </div>
  );
}

// Pulls the magic-link token out of /claim/<token>. Returns "" when the
// path doesn't match — caller renders an error state in that case.
function extractTokenFromPath() {
  const m = /^\/claim\/([^/?#]+)/.exec(window.location.pathname);
  return m ? decodeURIComponent(m[1]) : '';
}
